import { RefObject, useEffect, useRef, useState } from 'react'

interface UseGoodsListAutoScrollOptions {
  /** 每一段滚动的时长（毫秒），左->右、右->左各一次 */
  segmentDuration?: number
}

/**
 * 商品列表自动滚动 Hook
 * - 在 enabled 为 true 且未播放过的情况下，从左滚到最右再滚回最左，仅播放一次
 * @returns isScrolling 是否正在执行自动滚动动画
 */
const useGoodsListAutoScroll = (
  containerRef: RefObject<HTMLElement>,
  enabled: boolean,
  _deps: any[] = [],
  options: UseGoodsListAutoScrollOptions = {}
) => {
  const hasPlayedRef = useRef(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const { segmentDuration = 900 } = options

  useEffect(() => {
    if (!enabled || hasPlayedRef.current) {
      setIsScrolling(false)
      return
    }

    const container = containerRef.current
    if (!container) {
      setIsScrolling(false)
      return
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth
    if (maxScrollLeft <= 0) {
      setIsScrolling(false)
      return
    }

    let cancelled = false
    const timeouts: number[] = []

    setIsScrolling(true)

    // 第一段：滚到最右
    container.scrollTo({ left: maxScrollLeft, behavior: 'smooth' })
    const firstTimeout = window.setTimeout(() => {
      if (cancelled) return

      // 第二段：滚回最左
      container.scrollTo({ left: 0, behavior: 'smooth' })
      const secondTimeout = window.setTimeout(() => {
        if (!cancelled) {
          hasPlayedRef.current = true
          setIsScrolling(false)
        }
      }, segmentDuration)

      timeouts.push(secondTimeout)
    }, segmentDuration)

    timeouts.push(firstTimeout)

    return () => {
      cancelled = true
      setIsScrolling(false)
      timeouts.forEach((id) => window.clearTimeout(id))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, containerRef, segmentDuration, ..._deps])

  return { isScrolling }
}

export default useGoodsListAutoScroll
